export interface CodeExample {
  curl: {
    basic: string;
    streaming: string;
    conversation: string;
  };
  nodejs: {
    basic: string;
    streaming: string;
    conversation: string;
  };
  typescript: {
    basic: string;
    streaming: string;
    conversation: string;
  };
  python: {
    basic: string;
    streaming: string;
    conversation: string;
  };
  python_sdk: {
    basic: string;
    streaming: string;
    conversation: string;
  };
}

export const codeExamples: CodeExample = {
  curl: {
    basic: `curl "https://api.etherworks.cn/v1/chat/completions" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "YOUR_MODEL_ID",
    "messages": [
      {"role": "user", "content": "Explain quantum computing in simple terms"}
    ]
  }'`,

    streaming: `BASE_URL="https://api.etherworks.cn/v1"
API_KEY="YOUR_API_KEY"

# 1. Fetch the available model list
curl "$BASE_URL/models" \\
  -H "Authorization: Bearer $API_KEY"

# 2. Pick one model id from the response above
MODEL_ID="YOUR_MODEL_ID"

# 3. Request a streaming response
curl "$BASE_URL/chat/completions" \\
  -N \\
  -H "Authorization: Bearer $API_KEY" \\
  -H "Content-Type: application/json" \\
  -H "Accept: text/event-stream" \\
  -d '{
    "model": "'"$MODEL_ID"'",
    "messages": [
      {"role": "user", "content": "Write a haiku about AI"}
    ],
    "stream": true,
    "max_tokens": 1024
  }'`,

    conversation: `BASE_URL="https://api.etherworks.cn/v1"
API_KEY="YOUR_API_KEY"

# 1. Fetch the available model list
curl "$BASE_URL/models" \\
  -H "Authorization: Bearer $API_KEY"

# 2. Pick one model id from the response above
MODEL_ID="YOUR_MODEL_ID"

# 3. Send a multi-turn conversation
curl "$BASE_URL/chat/completions" \\
  -H "Authorization: Bearer $API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "'"$MODEL_ID"'",
    "messages": [
      {"role": "system", "content": "You are a helpful assistant."},
      {"role": "user", "content": "What is the capital of France?"},
      {"role": "assistant", "content": "The capital of France is Paris."},
      {"role": "user", "content": "What is its population?"}
    ],
    "temperature": 0.7,
    "max_tokens": 1024
  }'`
  },

  nodejs: {
    basic: `const BASE_URL = 'https://api.etherworks.cn/v1';
const API_KEY  = 'YOUR_API_KEY';
const headers  = { Authorization: \`Bearer \${API_KEY}\`, 'Content-Type': 'application/json' };

async function main() {
  // 1. Fetch the available model list
  const models = await fetch(\`\${BASE_URL}/models\`, { headers }).then(r => r.json());
  const modelId = models.data[0].id;
  console.log('Using model:', modelId);

  // 2. Send a chat completion request
  const res = await fetch(\`\${BASE_URL}/chat/completions\`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      model: modelId,
      messages: [{ role: 'user', content: 'Explain quantum computing in simple terms' }],
      temperature: 0.7,
      max_tokens: 1024,
    }),
  });

  const data = await res.json();
  console.log(data.choices[0].message.content);
}

main().catch(console.error);`,

    streaming: `const BASE_URL = 'https://api.etherworks.cn/v1';
const API_KEY  = 'YOUR_API_KEY';
const headers  = { Authorization: \`Bearer \${API_KEY}\`, 'Content-Type': 'application/json' };

async function main() {
  const models = await fetch(\`\${BASE_URL}/models\`, { headers }).then(r => r.json());
  const modelId = models.data[0].id;
  console.log('Using model:', modelId);

  const res = await fetch(\`\${BASE_URL}/chat/completions\`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      model: modelId,
      messages: [{ role: 'user', content: 'Write a haiku about AI' }],
      stream: true,
      max_tokens: 1024,
    }),
  });

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    const lines = buffer.split('\\n');
    buffer = lines.pop();

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      const payload = line.slice(6);
      if (payload === '[DONE]') break;

      const chunk = JSON.parse(payload);
      const delta = chunk.choices[0]?.delta || {};
      if (delta.reasoning) process.stderr.write(delta.reasoning);
      if (delta.content)   process.stdout.write(delta.content);
    }
  }

  console.log();
}

main().catch(console.error);`,

    conversation: `const BASE_URL = 'https://api.etherworks.cn/v1';
const API_KEY  = 'YOUR_API_KEY';
const headers  = { Authorization: \`Bearer \${API_KEY}\`, 'Content-Type': 'application/json' };

async function chat(messages) {
  const models = await fetch(\`\${BASE_URL}/models\`, { headers }).then(r => r.json());
  const modelId = models.data[0].id;

  const res = await fetch(\`\${BASE_URL}/chat/completions\`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ model: modelId, messages, temperature: 0.7, max_tokens: 1024 }),
  });
  const data = await res.json();
  return data.choices[0].message.content;
}

async function main() {
  const history = [
    { role: 'system', content: 'You are a helpful assistant.' },
  ];

  // Turn 1
  history.push({ role: 'user', content: 'What is the capital of France?' });
  const reply1 = await chat(history);
  console.log('Assistant:', reply1);
  history.push({ role: 'assistant', content: reply1 });

  // Turn 2 — follow-up
  history.push({ role: 'user', content: 'What is its population?' });
  const reply2 = await chat(history);
  console.log('Assistant:', reply2);
}

main().catch(console.error);`
  },

  typescript: {
    basic: `const BASE_URL = 'https://api.etherworks.cn/v1';
const API_KEY  = 'YOUR_API_KEY';
const headers  = { Authorization: \`Bearer \${API_KEY}\`, 'Content-Type': 'application/json' };

interface Model { id: string; context_window: number | null }
interface Choice { message: { role: string; content: string } }

async function main(): Promise<void> {
  // 1. Fetch the available model list
  const modelsRes = await fetch(\`\${BASE_URL}/models\`, { headers });
  const { data: models }: { data: Model[] } = await modelsRes.json();
  const modelId = models[0].id;
  console.log('Using model:', modelId);

  // 2. Send a chat completion request
  const res = await fetch(\`\${BASE_URL}/chat/completions\`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      model: modelId,
      messages: [{ role: 'user', content: 'Explain quantum computing in simple terms' }],
      temperature: 0.7,
      max_tokens: 1024,
    }),
  });

  const data: { choices: Choice[] } = await res.json();
  console.log(data.choices[0].message.content);
}

main().catch(console.error);`,

    streaming: `const BASE_URL = 'https://api.etherworks.cn/v1';
const API_KEY  = 'YOUR_API_KEY';
const headers  = { Authorization: \`Bearer \${API_KEY}\`, 'Content-Type': 'application/json' };

interface Delta { content?: string; reasoning?: string }

async function main(): Promise<void> {
  const modelsRes = await fetch(\`\${BASE_URL}/models\`, { headers });
  const { data: models } = await modelsRes.json();
  const modelId: string = models[0].id;
  console.log('Using model:', modelId);

  const res = await fetch(\`\${BASE_URL}/chat/completions\`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      model: modelId,
      messages: [{ role: 'user', content: 'Write a haiku about AI' }],
      stream: true,
      max_tokens: 1024,
    }),
  });

  const reader = res.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    const lines = buffer.split('\\n');
    buffer = lines.pop()!;

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      const payload = line.slice(6);
      if (payload === '[DONE]') break;

      const chunk = JSON.parse(payload);
      const delta: Delta = chunk.choices[0]?.delta || {};
      if (delta.reasoning) process.stderr.write(delta.reasoning);
      if (delta.content)   process.stdout.write(delta.content);
    }
  }

  console.log();
}

main().catch(console.error);`,

    conversation: `const BASE_URL = 'https://api.etherworks.cn/v1';
const API_KEY  = 'YOUR_API_KEY';
const headers  = { Authorization: \`Bearer \${API_KEY}\`, 'Content-Type': 'application/json' };

interface Message { role: string; content: string }

async function chat(messages: Message[]): Promise<string> {
  const modelsRes = await fetch(\`\${BASE_URL}/models\`, { headers });
  const { data: models } = await modelsRes.json();
  const modelId: string = models[0].id;

  const res = await fetch(\`\${BASE_URL}/chat/completions\`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ model: modelId, messages, temperature: 0.7, max_tokens: 1024 }),
  });
  const data = await res.json();
  return data.choices[0].message.content;
}

async function main(): Promise<void> {
  const history: Message[] = [
    { role: 'system', content: 'You are a helpful assistant.' },
  ];

  // Turn 1
  history.push({ role: 'user', content: 'What is the capital of France?' });
  const reply1 = await chat(history);
  console.log('Assistant:', reply1);
  history.push({ role: 'assistant', content: reply1 });

  // Turn 2 — follow-up
  history.push({ role: 'user', content: 'What is its population?' });
  const reply2 = await chat(history);
  console.log('Assistant:', reply2);
}

main().catch(console.error);`
  },

  python: {
    basic: `import requests

BASE_URL = "https://api.etherworks.cn/v1"
API_KEY  = "YOUR_API_KEY"
HEADERS  = {"Authorization": f"Bearer {API_KEY}", "Content-Type": "application/json"}

# 1. Fetch the available model list
models = requests.get(f"{BASE_URL}/models", headers=HEADERS).json()
model_id = models["data"][0]["id"]
print(f"Using model: {model_id}")

# 2. Send a chat completion request
response = requests.post(
    f"{BASE_URL}/chat/completions",
    headers=HEADERS,
    json={
        "model": model_id,
        "messages": [{"role": "user", "content": "Explain quantum computing in simple terms"}],
        "temperature": 0.7,
        "max_tokens": 1024,
    },
)
response.raise_for_status()

print(response.json()["choices"][0]["message"]["content"])`,

    streaming: `import json
import requests

BASE_URL = "https://api.etherworks.cn/v1"
API_KEY  = "YOUR_API_KEY"
HEADERS  = {"Authorization": f"Bearer {API_KEY}", "Content-Type": "application/json"}

models = requests.get(f"{BASE_URL}/models", headers=HEADERS).json()
model_id = models["data"][0]["id"]
print(f"Using model: {model_id}")

with requests.post(
    f"{BASE_URL}/chat/completions",
    headers=HEADERS,
    json={
        "model": model_id,
        "messages": [{"role": "user", "content": "Write a haiku about AI"}],
        "stream": True,
        "max_tokens": 1024,
    },
    stream=True,
) as resp:
    resp.raise_for_status()

    for raw_line in resp.iter_lines(decode_unicode=True):
        if not raw_line or not raw_line.startswith("data: "):
            continue
        payload = raw_line[6:]
        if payload.strip() == "[DONE]":
            break

        chunk = json.loads(payload)
        delta = chunk["choices"][0].get("delta", {})
        if delta.get("content"):
            print(delta["content"], end="", flush=True)

print()`,

    conversation: `import requests

BASE_URL = "https://api.etherworks.cn/v1"
API_KEY  = "YOUR_API_KEY"
HEADERS  = {"Authorization": f"Bearer {API_KEY}", "Content-Type": "application/json"}

models = requests.get(f"{BASE_URL}/models", headers=HEADERS).json()
model_id = models["data"][0]["id"]

def chat(messages: list[dict]) -> str:
    resp = requests.post(
        f"{BASE_URL}/chat/completions",
        headers=HEADERS,
        json={"model": model_id, "messages": messages, "temperature": 0.7, "max_tokens": 1024},
    )
    resp.raise_for_status()
    return resp.json()["choices"][0]["message"]["content"]

history = [{"role": "system", "content": "You are a helpful assistant."}]

# Turn 1
history.append({"role": "user", "content": "What is the capital of France?"})
reply = chat(history)
print("Assistant:", reply)
history.append({"role": "assistant", "content": reply})

# Turn 2 — follow-up
history.append({"role": "user", "content": "What is its population?"})
reply = chat(history)
print("Assistant:", reply)`
  },

  python_sdk: {
    basic: `from openai import OpenAI

client = OpenAI(
    base_url="https://api.etherworks.cn/v1",
    api_key="YOUR_API_KEY",
)

# Fetch available models
models = client.models.list()
model_id = models.data[0].id
print(f"Using model: {model_id}")

response = client.chat.completions.create(
    model=model_id,
    messages=[{"role": "user", "content": "Explain quantum computing in simple terms"}],
    temperature=0.7,
    max_tokens=1024,
)

print(response.choices[0].message.content)`,

    streaming: `from openai import OpenAI

client = OpenAI(
    base_url="https://api.etherworks.cn/v1",
    api_key="YOUR_API_KEY",
)

models = client.models.list()
model_id = models.data[0].id
print(f"Using model: {model_id}")

stream = client.chat.completions.create(
    model=model_id,
    messages=[{"role": "user", "content": "Write a haiku about AI"}],
    stream=True,
    max_tokens=1024,
)

for chunk in stream:
    delta = chunk.choices[0].delta
    if delta.content:
        print(delta.content, end="", flush=True)

print()`,

    conversation: `from openai import OpenAI

client = OpenAI(
    base_url="https://api.etherworks.cn/v1",
    api_key="YOUR_API_KEY",
)

models = client.models.list()
model_id = models.data[0].id

def chat(messages: list[dict]) -> str:
    resp = client.chat.completions.create(
        model=model_id,
        messages=messages,
        temperature=0.7,
        max_tokens=1024,
    )
    return resp.choices[0].message.content

history = [{"role": "system", "content": "You are a helpful assistant."}]

# Turn 1
history.append({"role": "user", "content": "What is the capital of France?"})
reply = chat(history)
print("Assistant:", reply)
history.append({"role": "assistant", "content": reply})

# Turn 2 — follow-up
history.append({"role": "user", "content": "What is its population?"})
reply = chat(history)
print("Assistant:", reply)`
  }
};

export const getCodeExample = (language: keyof CodeExample, type: 'basic' | 'streaming' | 'conversation'): string => {
  return codeExamples[language]?.[type] || 'Example not found';
};

export const availableLanguages = Object.keys(codeExamples) as (keyof CodeExample)[];

export const availableExampleTypes = ['basic', 'streaming', 'conversation'] as const;
